import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import { useProveedoresMantenimiento } from "./hooks/useProveedoresMantenimiento";

const ProveedoresMantenimiento = () => {
  const { proveedores, isLoading, error, createProveedor } = useProveedoresMantenimiento();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    contacto: "",
    telefono: "",
    email: "",
    especialidad: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const handleOpenModal = () => {
    setFormErrors({});
    setGeneralError("");
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setFormErrors({});
    setGeneralError("");
    setFormData({
      nombre: "",
      contacto: "",
      telefono: "",
      email: "",
      especialidad: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});
    setGeneralError("");
    try {
      await createProveedor(formData);
      handleCloseModal();
    } catch (err) {
      console.error("Error creating proveedor:", err);
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const fieldErrors = {};
        let generalErr = "";
        errors.forEach((error) => {
          if (error.attr && error.attr !== "non_field_errors") {
            fieldErrors[error.attr] = error.detail;
          } else {
            generalErr += error.detail + " ";
          }
        });
        setFormErrors(fieldErrors);
        if (generalErr) setGeneralError(generalErr.trim());
      } else if (err.response?.data?.detail) {
        setGeneralError(err.response.data.detail);
      } else {
        setGeneralError("Error al crear el proveedor. Intente nuevamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center py-5">
          <SpinnerCitoPlus text="Cargando proveedores..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger">
            Error al cargar los proveedores. Por favor intente nuevamente.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title">Proveedores de Mantenimiento</h4>
              <div>
                <button className="btn btn-primary btn-sm me-2" onClick={handleOpenModal}>
                  <i className="bi bi-plus-lg me-2"></i>
                  Agregar Proveedor
                </button>
                <Link to="/activos" className="btn btn-secondary btn-sm">
                  <i className="bi bi-arrow-left me-2"></i>
                  Volver a Activos
                </Link>
              </div>
            </div>
            <div className="card-body">
              {proveedores.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                  <p className="mt-3 text-muted">No hay proveedores de mantenimiento registrados.</p>
                  <button className="btn btn-primary" onClick={handleOpenModal}>
                    <i className="bi bi-plus-lg me-2"></i>
                    Agregar Proveedor
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Contacto</th>
                        <th>Teléfono</th>
                        <th>Email</th>
                        <th>Especialidad</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proveedores.map((prov) => (
                        <tr key={prov.uuid}>
                          <td>{prov.nombre}</td>
                          <td>{prov.contacto || "-"}</td>
                          <td>{prov.telefono || "-"}</td>
                          <td>{prov.email || "-"}</td>
                          <td>{prov.especialidad || "-"}</td>
                          <td>
                            <span className={`badge bg-${prov.activo ? "success" : "secondary"}`}>
                              {prov.activo ? "Activo" : "Inactivo"}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-warning me-2">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-danger">
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Agregar Proveedor de Mantenimiento</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {generalError && (
              <div className="alert alert-danger" role="alert">
                {generalError}
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre del proveedor"
                required
                isInvalid={!!formErrors.nombre}
              />
              {formErrors.nombre && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.nombre}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contacto</Form.Label>
              <Form.Control
                type="text"
                name="contacto"
                value={formData.contacto}
                onChange={handleChange}
                placeholder="Nombre de la persona de contacto"
                isInvalid={!!formErrors.contacto}
              />
              {formErrors.contacto && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.contacto}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                type="text"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Teléfono de contacto"
                isInvalid={!!formErrors.telefono}
              />
              {formErrors.telefono && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.telefono}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Correo electrónico"
                isInvalid={!!formErrors.email}
              />
              {formErrors.email && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especialidad</Form.Label>
              <Form.Control
                type="text"
                name="especialidad"
                value={formData.especialidad}
                onChange={handleChange}
                placeholder="Ej: Mantenimiento de mobiliario"
                isInvalid={!!formErrors.especialidad}
              />
              {formErrors.especialidad && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.especialidad}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Guardando..." : "Guardar"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProveedoresMantenimiento;
